// event-journal.ts
//
// Journal JSONL append-only — NGUỒN SỰ THẬT của mission (§3.4). Một file, không
// rotation, không snapshot (YAGNI v1).
//
// Ba lớp bảo vệ writer đơn:
//   1. `locks/journal.lock` (O_EXCL, chứa pid) — chặn TIẾN TRÌNH thứ hai.
//   2. `AsyncMutexByKey` — xếp hàng các append song song TRONG cùng tiến trình
//      (lock file không phân biệt được 2 lời gọi cùng process ⇒ sẽ tự deadlock).
//   3. fd mở 'a' giữ suốt vòng đời — mọi append đi qua đúng một fd.
//
// `append()` resolve CHỈ SAU fsync. Throw ⇒ caller TUYỆT ĐỐI không được ack/publish.
// `EventJournal.open()` KHÔNG tự sửa file: nó trả cả `scan` để caller (mission-store)
// quyết định recovery hay vào RECOVERING.

import { promises as fs } from "node:fs";
import type { FileHandle } from "node:fs/promises";
import { dirname, join } from "node:path";
import { AsyncMutexByKey } from "../async-mutex-by-key.js";
import { ensureDir } from "../filesystem-helpers.js";
import { type DurableFsOps, realDurableFsOps, writeAllBytes } from "./durable-file-writer.js";
import { acquireJournalLease, fileExists } from "./journal-lease.js";
import { type JournalScanReport, scanJournal } from "./journal-scan-report.js";
import { sealRecord } from "./record-checksum.js";

export { JournalLeaseError } from "./journal-lease.js";

export interface JournalEventInput {
  event_id: string;
  operation_id?: string;
  expected_revision?: number;
  delta: Record<string, unknown>;
}

export interface AppendedRecord extends JournalEventInput {
  seq: number;
  event_checksum: string;
}

export interface EventJournalOpenOptions {
  ops?: DurableFsOps;
  /** Ngân sách chờ hàng đợi in-process (ms). Không phải ngưỡng runtime §18. */
  appendQueueTimeoutMs?: number;
}

const DEFAULT_APPEND_QUEUE_TIMEOUT_MS = 30_000;

export class EventJournal {
  private handle: FileHandle | undefined;
  private lastSeq: number;
  private closed = false;
  /** F6: đặt khi fsync ném — mọi append sau đó bị từ chối (xem `append`). */
  private poisoned = false;
  private readonly mutex = new AsyncMutexByKey();
  private readonly queueTimeoutMs: number;

  private constructor(
    readonly path: string,
    private readonly leasePath: string,
    handle: FileHandle,
    lastSeq: number,
    private readonly ops: DurableFsOps,
    queueTimeoutMs: number,
  ) {
    this.handle = handle;
    this.lastSeq = lastSeq;
    this.queueTimeoutMs = queueTimeoutMs;
  }

  /**
   * Mở journal. Trả `{journal, scan}` — KHÔNG tự sửa file hỏng.
   * `scan.status === "corrupt"` ⇒ caller phải vào RECOVERING và KHÔNG append.
   */
  static async open(
    journalPath: string,
    options: EventJournalOpenOptions = {},
  ): Promise<{ journal: EventJournal; scan: JournalScanReport }> {
    const ops = options.ops ?? realDurableFsOps;
    const dir = dirname(journalPath);
    await ensureDir(dir);
    const leasePath = join(dir, "locks", "journal.lock");
    await ensureDir(dirname(leasePath));
    await acquireJournalLease(leasePath);

    try {
      const scan = await scanJournal(journalPath);
      const existed = await fileExists(journalPath);
      const handle = await ops.open(journalPath, "a");
      // Dir fsync CHỈ khi dir entry đổi = lúc TẠO file. Append sau đó không đổi
      // dir entry ⇒ không fsync thư mục nữa (§3.4 / test 27).
      if (!existed) await ops.fsyncDir(dir);
      const journal = new EventJournal(
        journalPath,
        leasePath,
        handle,
        scan.lastSeq,
        ops,
        options.appendQueueTimeoutMs ?? DEFAULT_APPEND_QUEUE_TIMEOUT_MS,
      );
      return { journal, scan };
    } catch (err) {
      await fs.unlink(leasePath).catch(() => {});
      throw err;
    }
  }

  get currentSeq(): number {
    return this.lastSeq;
  }

  /**
   * Append một BATCH event. Toàn bộ batch được ghi bằng một lời gọi write-loop rồi
   * MỘT lần fsync — resolve chỉ sau khi fsync xong.
   *
   * Lỗi giữa chừng ⇒ `lastSeq` KHÔNG nhích (đuôi dở sẽ được recovery cắt bỏ), và
   * lỗi được ném lên để caller không ack.
   */
  async append(events: JournalEventInput[]): Promise<AppendedRecord[]> {
    if (events.length === 0) return [];
    const release = await this.mutex.acquire(
      this.path,
      Date.now() + this.queueTimeoutMs,
      () => new Error(`EventJournal: chờ quá ${this.queueTimeoutMs}ms để tới lượt append.`),
    );
    try {
      if (this.closed || this.handle === undefined) {
        throw new Error("EventJournal: journal đã đóng.");
      }
      // F6: một lần fsync lỗi là đủ để không còn biết chắc đuôi file chứa gì.
      if (this.poisoned) {
        throw new Error(
          "EventJournal: fsync đã thất bại ở một append trước đó — journal KHÔNG còn " +
            "đáng tin (byte có thể đã trên đĩa trong khi lastSeq chưa nhích). " +
            "Append tiếp sẽ phát lại seq đã dùng. Cần người xem xét journal.",
        );
      }
      const records: AppendedRecord[] = [];
      let payload = "";
      let seq = this.lastSeq;
      for (const event of events) {
        seq += 1;
        const record = sealRecord({
          seq,
          event_id: event.event_id,
          operation_id: event.operation_id,
          expected_revision: event.expected_revision ?? seq - 1,
          delta: event.delta,
        }) as unknown as AppendedRecord;
        records.push(record);
        payload += `${JSON.stringify(record)}\n`;
      }
      await writeAllBytes(this.handle, Buffer.from(payload, "utf8"), this.ops);
      // fsync TRƯỚC khi resolve — không có ngoại lệ nào (§3.4).
      //
      // Audit 2026-09-14 (F6): fsync lỗi ⇒ journal thành KHÔNG DÙNG ĐƯỢC, vĩnh
      // viễn. Trước fix, `lastSeq` không nhích khi fsync ném, nhưng byte có thể
      // ĐÃ nằm trên đĩa ⇒ append kế phát lại đúng seq đó ⇒ journal
      // `seq_duplicate` (đo được: append thứ ba ghi seq 2 lần hai). Journal là
      // nguồn sự thật của mission; seq trùng âm thầm còn tệ hơn dừng hẳn.
      try {
        await this.ops.fsyncFile(this.handle);
      } catch (err) {
        this.poisoned = true;
        throw err;
      }
      this.lastSeq = seq;
      return records;
    } finally {
      release();
    }
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    const handle = this.handle;
    this.handle = undefined;
    if (handle) await this.ops.close(handle).catch(() => {});
    await fs.unlink(this.leasePath).catch(() => {});
  }
}
